/**
 * WP-06C-B — The product's only access path to Loops.
 *
 * Cutover contract, enforced structurally by this module:
 *
 *   * every Loop the product renders comes from a hosted authority read;
 *   * every Loop change goes through an authenticated server command; nothing
 *     writes Loop state locally, optimistically, or in the background;
 *   * after any successful mutation the affected queries are INVALIDATED and
 *     re-read, so the screen shows what the backend decided rather than what the
 *     browser hoped for. Backend-owned status, `record_version`, and timestamps
 *     are therefore never predicted here;
 *   * `record_version` is taken from the last authoritative read and sent as the
 *     caller's optimistic EXPECTATION. On `version_conflict` the cache is
 *     refreshed and the user is told to look again — the request is never
 *     retried with a version this layer invented;
 *   * failures surface as categorical, pre-written sentences (see
 *     ./loopMessages). No provider message, SQL text, identifier, token, or raw
 *     exception can reach the UI through this module.
 *
 * React Query is a transient view cache only: no Loop state is persisted, and
 * nothing here reads or writes localStorage.
 */

import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useCallback, useMemo, useState } from "react";

import type { LoopCreationRequest } from "../creation/creationCommand";
import type { LoopLifecycleFailureReason } from "../lifecycle/errors";
import type { LoopLifecycleRequest } from "../lifecycle/lifecycleCommand";
import { runLoopLifecycleCommand } from "../lifecycle/lifecycle.functions";
import { createHostedLoop, getHostedLoopCommand, listHostedLoopsCommand } from "../loops.functions";
import type { HostedLoopCondition } from "../read/loopProjection";
import {
  creationFailureMessage,
  lifecycleFailureMessage,
  readFailureMessage,
} from "./loopMessages";
import { hostedDetailToLoopView, hostedSummaryToLoopView } from "./loopView";
import type { Loop } from "@/lib/looseend/types";

const LOOPS_KEY = ["hostedLoops"] as const;
const loopKey = (loopId: string) => ["hostedLoop", loopId] as const;

/** Where the hosted list currently stands. `loading` renders a skeleton, never data. */
export type HostedLoopsPhase = "loading" | "ready" | "unavailable";

export interface HostedLoopsView {
  readonly phase: HostedLoopsPhase;
  /** Empty while loading or unavailable: an empty list is never mistaken for truth. */
  readonly loops: readonly Loop[];
  readonly message: string | null;
  readonly refresh: () => void;
}

export interface HostedLoopDetailView {
  readonly phase: "loading" | "ready" | "not_found" | "unavailable";
  readonly loop: Loop | null;
  readonly conditions: readonly HostedLoopCondition[];
  readonly message: string | null;
  readonly refresh: () => void;
}

/** Every mutation answers with this shape. No exception ever escapes. */
export type HostedMutationResult =
  | {
      readonly ok: true;
      readonly replayed: boolean;
      /**
       * The version the AUTHORITY reported after applying the command. It is
       * never computed here; it exists so a multi-step sequence (verify each
       * condition, then resolve) can carry the authority's own latest version
       * instead of the now-stale one it started from.
       */
      readonly recordVersion: number;
    }
  | {
      readonly ok: false;
      readonly message: string;
      readonly retryable: boolean;
      /** The categorical reason, so a surface may phrase the refusal for its gesture. */
      readonly reason?: LoopLifecycleFailureReason;
    };

async function invalidateLoop(client: QueryClient, loopId?: string): Promise<void> {
  await client.invalidateQueries({ queryKey: LOOPS_KEY });
  if (loopId) await client.invalidateQueries({ queryKey: loopKey(loopId) });
}

/** The authoritative Loop list. */
export function useHostedLoops(): HostedLoopsView {
  const listLoops = useServerFn(listHostedLoopsCommand);
  const client = useQueryClient();
  const query = useQuery({
    queryKey: LOOPS_KEY,
    queryFn: () => listLoops({}),
    staleTime: 15_000,
    retry: 1,
  });

  const refresh = useCallback(() => {
    void client.invalidateQueries({ queryKey: LOOPS_KEY });
  }, [client]);

  return useMemo<HostedLoopsView>(() => {
    if (query.isPending) return { phase: "loading", loops: [], message: null, refresh };
    const outcome = query.data;
    if (query.isError || outcome === undefined) {
      return {
        phase: "unavailable",
        loops: [],
        message: readFailureMessage("unavailable"),
        refresh,
      };
    }
    if (!outcome.ok) {
      return {
        phase: "unavailable",
        loops: [],
        message: readFailureMessage(outcome.reason),
        refresh,
      };
    }
    return {
      phase: "ready",
      loops: outcome.loops.map(hostedSummaryToLoopView),
      message: null,
      refresh,
    };
  }, [query.isPending, query.isError, query.data, refresh]);
}

/** One authoritative Loop, including its governed resolution conditions. */
export function useHostedLoopDetail(loopId: string | null): HostedLoopDetailView {
  const getLoop = useServerFn(getHostedLoopCommand);
  const client = useQueryClient();
  const query = useQuery({
    queryKey: loopKey(loopId ?? "none"),
    queryFn: () => getLoop({ data: { loopId: loopId as string } }),
    enabled: loopId !== null,
    staleTime: 5_000,
    retry: 1,
  });

  const refresh = useCallback(() => {
    if (loopId) void client.invalidateQueries({ queryKey: loopKey(loopId) });
  }, [client, loopId]);

  return useMemo<HostedLoopDetailView>(() => {
    if (loopId === null) {
      return { phase: "not_found", loop: null, conditions: [], message: null, refresh };
    }
    if (query.isPending) {
      return { phase: "loading", loop: null, conditions: [], message: null, refresh };
    }
    const outcome = query.data;
    if (query.isError || outcome === undefined) {
      return {
        phase: "unavailable",
        loop: null,
        conditions: [],
        message: readFailureMessage("unavailable"),
        refresh,
      };
    }
    if (!outcome.ok) {
      return {
        phase: outcome.reason === "not_found" ? "not_found" : "unavailable",
        loop: null,
        conditions: [],
        message: readFailureMessage(outcome.reason),
        refresh,
      };
    }
    return {
      phase: "ready",
      loop: hostedDetailToLoopView(outcome.loop),
      conditions: outcome.loop.conditions,
      message: null,
      refresh,
    };
  }, [loopId, query.isPending, query.isError, query.data, refresh]);
}

export interface HostedLoopActions {
  readonly busy: boolean;
  /** Create a Loop from governed content. Returns the backend's new Loop id. */
  readonly create: (
    request: LoopCreationRequest,
  ) => Promise<
    | { readonly ok: true; readonly loopId: string; readonly replayed: boolean }
    | { readonly ok: false; readonly message: string; readonly retryable: boolean }
  >;
  /** Request a status transition. `open|waiting|action_needed|blocked|ready_to_verify`. */
  readonly transition: (
    loop: Pick<Loop, "id" | "recordVersion">,
    targetStatus: Loop["status"],
    reason?: string,
  ) => Promise<HostedMutationResult>;
  readonly snooze: (
    loop: Pick<Loop, "id" | "recordVersion">,
    snoozedUntilIso: string,
  ) => Promise<HostedMutationResult>;
  readonly unsnooze: (loop: Pick<Loop, "id" | "recordVersion">) => Promise<HostedMutationResult>;
  /** Resolve. The backend refuses unless its own verification requirements are met. */
  readonly resolve: (loop: Pick<Loop, "id" | "recordVersion">) => Promise<HostedMutationResult>;
  readonly archive: (loop: Pick<Loop, "id" | "recordVersion">) => Promise<HostedMutationResult>;
  /**
   * WP-06R: reopen a resolved Loop into a new resolution cycle. The backend is
   * the only authority on eligibility, the cycle number, and condition resets.
   */
  readonly reopen: (
    loop: Pick<Loop, "id" | "recordVersion">,
    reason?: string,
  ) => Promise<HostedMutationResult>;
  readonly verifyCondition: (
    loop: Pick<Loop, "id" | "recordVersion">,
    conditionId: string,
    method: "human_attestation" | "evidence",
    evidenceId?: string,
  ) => Promise<HostedMutationResult>;
}

/**
 * The single write path. Each call sends the LAST READ `record_version` as an
 * expectation and re-reads afterwards; nothing is applied locally on the way.
 */
export function useHostedLoopActions(): HostedLoopActions {
  const runLifecycle = useServerFn(runLoopLifecycleCommand);
  const createLoop = useServerFn(createHostedLoop);
  const client = useQueryClient();
  const [pending, setPending] = useState(0);

  const lifecycle = useCallback(
    async (
      loop: Pick<Loop, "id" | "recordVersion">,
      request: LoopLifecycleRequest,
    ): Promise<HostedMutationResult> => {
      setPending((n) => n + 1);
      try {
        const outcome = await runLifecycle({
          data: { loopId: loop.id, expectedVersion: loop.recordVersion, request },
        });
        if (outcome.ok) {
          await invalidateLoop(client, loop.id);
          return {
            ok: true,
            replayed: outcome.record.replayed,
            recordVersion: outcome.record.recordVersion,
          };
        }
        // A stale expectation is corrected by re-reading authority, never by
        // resending with a version this layer guessed.
        if (outcome.reason === "version_conflict") await invalidateLoop(client, loop.id);
        return {
          ok: false,
          message: lifecycleFailureMessage(outcome.reason),
          retryable: outcome.retryable,
          reason: outcome.reason,
        };
      } catch {
        // The raw failure is deliberately swallowed: transport detail is not
        // product information.
        return {
          ok: false,
          message: lifecycleFailureMessage("unavailable"),
          retryable: true,
          reason: "unavailable",
        };
      } finally {
        setPending((n) => n - 1);
      }
    },
    [client, runLifecycle],
  );

  const create = useCallback<HostedLoopActions["create"]>(
    async (request) => {
      setPending((n) => n + 1);
      try {
        const outcome = await createLoop({ data: { request } });
        if (outcome.ok) {
          await invalidateLoop(client, outcome.record.loopId);
          return {
            ok: true,
            loopId: outcome.record.loopId,
            replayed: outcome.record.replayed,
          };
        }
        return {
          ok: false,
          message: creationFailureMessage(outcome.reason),
          retryable: outcome.retryable,
        };
      } catch {
        return { ok: false, message: creationFailureMessage("unavailable"), retryable: true };
      } finally {
        setPending((n) => n - 1);
      }
    },
    [client, createLoop],
  );

  return useMemo<HostedLoopActions>(
    () => ({
      busy: pending > 0,
      create,
      transition: (loop, targetStatus, reason) =>
        lifecycle(loop, {
          kind: "transitionStatus",
          targetStatus,
          ...(reason === undefined ? {} : { reason }),
        }),
      snooze: (loop, snoozedUntilIso) =>
        lifecycle(loop, { kind: "snooze", snoozedUntil: snoozedUntilIso }),
      unsnooze: (loop) => lifecycle(loop, { kind: "unsnooze" }),
      resolve: (loop) => lifecycle(loop, { kind: "resolve" }),
      archive: (loop) => lifecycle(loop, { kind: "archive" }),
      reopen: (loop, reason) =>
        lifecycle(loop, { kind: "reopen", ...(reason === undefined ? {} : { reason }) }),
      verifyCondition: (loop, conditionId, method, evidenceId) =>
        lifecycle(loop, {
          kind: "verifyCondition",
          conditionId,
          method,
          ...(method === "evidence" && evidenceId !== undefined ? { evidenceId } : {}),
        }),
    }),
    [create, lifecycle, pending],
  );
}
